import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Loader2, ArrowLeft, Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import AuthLayout from '../components/AuthLayout';
import { forcePWAUpdate, disablePWAForAuth, isPWAContext } from '../utils/pwaUtils';
import { motion } from 'framer-motion';
import { getSelectedService } from '../lib/catalog';

const LoginPage = () => {
  const { login, isAuthenticated, user } = useAuth();
  const selectedService = getSelectedService();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwaCleaned, setPwaCleaned] = useState(false);

  // PWA cache cleaning désactivé temporairement pour éviter les boucles
  // useEffect(() => {
  //   const cleanPWACache = async () => {
  //     if (isPWAContext() && !pwaCleaned) {
  //       // console.log('[LOGIN] PWA context detected, cleaning cache...');
  //       setTimeout(() => {
  //         forcePWAUpdate();
  //         disablePWAForAuth();
  //       }, 100);
  //       setPwaCleaned(true);
  //     }
  //   };
  //   
  //   cleanPWACache();
  // }, [pwaCleaned]);

  if (isAuthenticated && user) {
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/mon-espace'} replace />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        const targetPath = result.user?.role === 'admin' ? '/admin' : '/mon-espace';
        window.location.href = targetPath;
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Erreur inattendue lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return <AuthLayout title={<>Heureuse de<br /><em>vous retrouver.</em></>} description="Connectez-vous pour réserver et retrouver vos rendez-vous.">
    {selectedService && <p className="korean-auth-selection">Votre soin : <strong>{selectedService.name}</strong> · {selectedService.price} €</p>}
    <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Alert className="border-red-500/50 bg-red-500/10 backdrop-blur-sm">
                        <AlertCircle className="h-4 w-4 text-red-400" />
                        <AlertDescription className="text-red-400 text-sm font-medium">{error}</AlertDescription>
                      </Alert>
                    </motion.div>
                  )}
                  
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-stone-700 font-light text-sm flex items-center gap-2">
                      <Mail className="w-4 h-4 text-amber-600" />
                      Adresse email
                    </Label>
                    <div className="relative group">
                      <Input
                        id="email"
                        name="email"
                        type="email" autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        required
                        disabled={loading}
                        className="h-12 pl-12 pr-4 border-2 border-stone-200 focus:border-amber-400 bg-white/80 backdrop-blur-sm text-stone-700 rounded-xl text-base transition-all duration-200 placeholder:text-stone-400 focus:ring-2 focus:ring-amber-400/20 font-light"
                      />
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400 group-focus-within:text-amber-600 transition-colors" />
                    </div>
                  </div>
                  
                  {/* Password Field */}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-stone-700 font-light text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-600" />
                      Mot de passe
                    </Label>
                    <div className="relative group">
                      <Input
                        id="password"
                        name="password" autoComplete="current-password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        disabled={loading}
                        className="h-12 pl-12 pr-12 border-2 border-stone-200 focus:border-amber-400 bg-white/80 backdrop-blur-sm text-stone-700 rounded-xl text-base transition-all duration-200 placeholder:text-stone-400 focus:ring-2 focus:ring-amber-400/20 font-light"
                      />
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400 group-focus-within:text-amber-600 transition-colors" />
                      <button
                        type="button"
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-stone-400 hover:text-amber-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-light rounded-full transition-all duration-300 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 disabled:opacity-50 disabled:cursor-not-allowed text-base relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      {loading ? (
                        <>
                          <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                          Connexion en cours...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-5 w-5" />
                          Se connecter
                        </>
                      )}
                    </Button>
                  </motion.div>
                </form>
    <div className="korean-auth-links"><Link to="/mot-de-passe-oublie">Mot de passe oublié ?</Link><p>Première visite ? <Link to="/inscription">Créer mon compte</Link></p></div>
  </AuthLayout>;
};
export default LoginPage;
